import { animated, useTransition } from "@react-spring/web";

import { Box, Button, Stack, styled, useMediaQuery } from "@mui/material";

import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { transparent } from "@app/common/theme/colors";

import { Row } from "@rui/Stack";
import Stepper from "@rui/Stepper";

import theme from "../theme";

const ButtonsStripe = styled(Stack)(({ theme }) => ({
  gap: theme.spaces.small,
  flexDirection: "row",
}));

const CarouselNotificationBanner = ({
  localStorageName,
  children,
  button,
  activeStepIndex,
  setActiveStepIndex,
  steps,
}: {
  localStorageName?: string;
  children: React.ReactNode;
  button?: React.ReactNode;
  activeStepIndex: number;
  setActiveStepIndex: (i: number) => void;
  steps: number;
}) => {
  const isSmallView = useMediaQuery(theme.breakpoints.down("sm"));
  const [notificationBannerDismissed, setNotificationBannerDismissed] =
    useLocalStorage(localStorageName ?? "", false);

  const handleDismiss = () => button ?? setNotificationBannerDismissed(true);

  const transitions = useTransition(!notificationBannerDismissed, {
    from: { opacity: 0, height: 0, minHeight: 0, marginBottom: 0 },
    enter: {
      opacity: 1,
      height: "auto",
      minHeight: 76,
      marginBottom: isSmallView ? 8 : 16,
    },
    leave: { opacity: 0, height: 0, minHeight: 0, marginBottom: 0 },
  });

  const handleNextStep = () => {
    if (activeStepIndex < steps - 1) {
      setActiveStepIndex(activeStepIndex + 1);
    } else {
      // reset stepper back to start after final step
      setActiveStepIndex(0);
    }
  };

  const stepper = (
    <Stepper
      sx={{
        height: "8px",
        m: "2px",
        mt: "4px",
      }}
      inactiveStepWidth="8px"
      activeStepWidth="16px"
      inactiveStepColor={transparent.white[30]}
      activeStepColor="white"
      steps={steps}
      activeStep={activeStepIndex}
      onClickStep={(i) => setActiveStepIndex(i)}
    />
  );

  return (
    <div>
      {transitions((style, shouldShow) => {
        return (
          shouldShow && (
            <animated.div
              style={{
                overflow: "hidden",
                marginRight: isSmallView ? "-16px" : undefined,
                marginLeft: isSmallView ? "-16px" : undefined,
                marginTop: isSmallView ? "8px" : undefined,
                ...style,
              }}
            >
              <Stack
                alignItems={isSmallView ? "left" : "center"}
                direction={isSmallView ? "column" : "row"}
                justifyContent="space-between"
                sx={{
                  minWidth: "100%",
                  width: "100%",
                  p: "12px 20px",
                  bgcolor: "shades.primary.800",
                  borderRadius: { xxs: "0", sm: "6px" },
                }}
                gap={isSmallView ? theme.spaces.base : undefined}
              >
                <Box component="div">
                  {children}
                  {!isSmallView && stepper}
                </Box>

                <Row
                  justifyContent={isSmallView ? "space-between" : undefined}
                  alignItems={isSmallView ? "center" : undefined}
                >
                  <ButtonsStripe>
                    <Button
                      variant="contained"
                      sx={{ minWidth: "100px", height: "44px" }}
                      onClick={handleNextStep}
                    >
                      Next Tip
                    </Button>
                    <Button
                      variant="text"
                      sx={{ minWidth: "100px", height: "44px", color: "white" }}
                      onClick={handleDismiss}
                    >
                      Dismiss
                    </Button>
                  </ButtonsStripe>
                  {isSmallView && stepper}
                </Row>
              </Stack>
            </animated.div>
          )
        );
      })}
    </div>
  );
};

export default CarouselNotificationBanner;
